<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import {
  DocumentState,
  SigningRequestStatus,
  type SigningRequestStatusEntry,
  type SigningRequestWithRelationsInDb,
  type UserInDbSlim,
  type VaultFileRevisionInDb,
} from '@/api';
import { signingRequestRoute } from '@/router/links';
import { formatDateTimeForTable } from '@/utils/datetimeUtils';
import GaiaButton from '../../base/GaiaButton.vue';
import GaiaIconButton from '../../base/GaiaIconButton.vue';
import GaiaLink from '../../base/GaiaLink.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaSelect from '../../base/selects/GaiaSelect.vue';
import GaiaPanel from '../../composite/GaiaPanel.vue';
import GaiaTimeline, { type GaiaTimelineItem } from '../../composite/GaiaTimeline.vue';
import { IconChevronBack, IconChevronForward, IconClose } from '../../icons';
import GaiaContractStateCell from './GaiaContractStateCell.vue';
import { LOCKED_DOCUMENT_STATES, documentStateLabels } from './contractStateIcons';

const props = defineProps({
  /** The page opens and closes Layout's `#drawer` slot with this. */
  show: {
    type: Boolean,
    default: false,
  },
  /**
   * Edit mode, not the history (v-model:editing). The page sets it on open —
   * the Status cell's pencil opens straight into it, even when already open.
   */
  editing: {
    type: Boolean,
    default: false,
  },
  /** Newest first, as the vault lists them. */
  revisions: {
    type: Array as PropType<VaultFileRevisionInDb[]>,
    required: true,
  },
  /** The version being read — its state and signatures show, as the Status cell's do. */
  readRevision: {
    type: Object as PropType<VaultFileRevisionInDb | null>,
    default: null,
  },
  /** The file the history is read for — its section's head. */
  fileName: {
    type: String,
    required: true,
  },
  /**
   * The file's signing requests, each with its status changes. Null while
   * loading; empty when it was never sent (or the caller can't see signing).
   */
  signingHistory: {
    type: Array as PropType<
      { request: SigningRequestWithRelationsInDb; entries: SigningRequestStatusEntry[] }[] | null
    >,
    default: null,
  },
  /** Why the caller can't set a state; null when they can. */
  writeBlock: {
    type: String as PropType<string | null>,
    default: null,
  },
});

const emit = defineEmits<{
  'update:show': [value: boolean];
  'update:editing': [value: boolean];
  setState: [revisionUid: string, state: DocumentState];
}>();

const editing = computed({
  get: () => props.editing && !props.writeBlock,
  set: value => {
    emit('update:editing', value);
  },
});

const stateOptions = Object.values(DocumentState)
  .filter(state => !LOCKED_DOCUMENT_STATES.includes(state))
  .map(state => ({ label: documentStateLabels[state], value: state }));

function byUser(user: UserInDbSlim | null | undefined): string | undefined {
  return user ? `by ${user.full_name || user.email}` : undefined;
}

/** The steps of a signing request worth a line; drafting and waiting are not. */
const STEP_LABELS: Partial<Record<SigningRequestStatus, string>> = {
  [SigningRequestStatus.PENDING]: 'Sent for signature',
  [SigningRequestStatus.SIGNED]: 'Completed',
  [SigningRequestStatus.REJECTED]: 'Rejected',
  [SigningRequestStatus.EXPIRED]: 'Expired',
  [SigningRequestStatus.ERROR]: 'Failed',
  [SigningRequestStatus.CANCELLED]: 'Cancelled',
};

/**
 * A request's steps. Sending is the move to pending; a request with no
 * pending recorded went straight to waiting, so it was sent then.
 */
function requestSteps(
  requestUid: string,
  entries: SigningRequestStatusEntry[]
): { at: string | null; item: GaiaTimelineItem }[] {
  const pendingRecorded = entries.some(entry => entry.status === SigningRequestStatus.PENDING);
  return entries.flatMap((entry, index) => {
    const label =
      entry.status === SigningRequestStatus.WAITING_FOR_SIGNATURES && !pendingRecorded
        ? 'Sent for signature'
        : entry.status
          ? STEP_LABELS[entry.status]
          : undefined;
    if (!label) return [];
    return [
      {
        at: entry.changed_at,
        item: {
          key: `${requestUid}:${index}`,
          label,
          detail: byUser(entry.changed_by_user),
          time: entry.changed_at ? formatDateTimeForTable(entry.changed_at) : undefined,
          done: true,
        },
      },
    ];
  });
}

const current = computed(() => props.readRevision);

/** The file's requests, oldest first, so the arrows walk forward in time. */
const requests = computed(() =>
  [...(props.signingHistory ?? [])].sort((a, b) =>
    a.request.created_at.localeCompare(b.request.created_at)
  )
);

/** The request on show; each load opens on the newest. */
const requestIndex = ref(0);
watch(requests, list => (requestIndex.value = Math.max(0, list.length - 1)), { immediate: true });

const shown = computed(() => requests.value.at(requestIndex.value) ?? null);

/**
 * The shown request, start to end: when it was created and by whom (its
 * draft step; the request itself when none was recorded), then its steps in
 * the order they happened. A file signed outside Gaia has no requests — its
 * creation and the signatures found on it stand in for them.
 */
const events = computed<GaiaTimelineItem[]>(() => {
  if (!shown.value) {
    const first = props.revisions.at(-1);
    return [
      ...(first
        ? [
            {
              key: 'created',
              label: 'Created',
              detail: byUser(first.created_by_user),
              time: formatDateTimeForTable(first.created_at),
              done: true,
            },
          ]
        : []),
      ...(current.value?.signature_infos ?? []).map(info => ({
        key: info.uid,
        label: info.signer ? `Signed by ${info.signer}` : 'Signed',
        detail: info.authority ?? undefined,
        time: info.signing_date ? formatDateTimeForTable(info.signing_date) : undefined,
        done: true,
      })),
    ];
  }
  const { request, entries } = shown.value;
  const draft = entries.find(entry => entry.status === SigningRequestStatus.DRAFT);
  const createdAt = draft?.changed_at ?? request.created_at;
  const steps = requestSteps(request.uid, entries).sort((a, b) =>
    (a.at ?? '').localeCompare(b.at ?? '')
  );
  return [
    {
      key: `${request.uid}:created`,
      label: 'Created',
      detail: byUser(draft?.changed_by_user),
      time: formatDateTimeForTable(createdAt),
      done: true,
    },
    ...steps.map(step => step.item),
  ];
});

function isLocked(revision: VaultFileRevisionInDb): boolean {
  return LOCKED_DOCUMENT_STATES.includes(revision.document_state);
}
</script>

<template>
  <!-- The signing-history side panel of the Contract View mock, for this one
       file: its state, then one signing request at a time — when it was
       created, sent, completed, … — with arrows through the file's others.
       Edit sets a version's state by hand, as the ⋯ menu's "Set state" does. Mounted in Layout's `#drawer` slot, so it
       pushes the page aside. -->
  <gaia-panel>
    <template #header>
      <n-flex align="flex-start" :size="12" :wrap="false" :style="{ width: '100%' }">
        <gaia-text tag="div" size="subtitle" weight="semibold" tone="ink" :style="{ minWidth: 0 }">
          {{ editing ? 'Edit signature status' : 'Signing history' }}
        </gaia-text>
        <!-- Through the file's signing requests, once there are several. -->
        <n-flex
          v-if="!editing && requests.length > 1"
          :size="2"
          :wrap="false"
          :style="{ marginLeft: 'auto', flex: 'none' }"
        >
          <gaia-icon-button
            :dimension="28"
            quaternary
            aria-label="Previous signing request"
            :disabled="requestIndex === 0"
            @click="requestIndex--"
          >
            <n-icon :component="IconChevronBack" :size="14" />
          </gaia-icon-button>
          <gaia-icon-button
            :dimension="28"
            quaternary
            aria-label="Next signing request"
            :disabled="requestIndex === requests.length - 1"
            @click="requestIndex++"
          >
            <n-icon :component="IconChevronForward" :size="14" />
          </gaia-icon-button>
        </n-flex>
        <gaia-button
          size="small"
          :disabled="Boolean(props.writeBlock)"
          :title="props.writeBlock ?? undefined"
          :style="{
            marginLeft: !editing && requests.length > 1 ? undefined : 'auto',
            flex: 'none',
          }"
          @click="editing = !editing"
        >
          {{ editing ? 'Done' : 'Edit' }}
        </gaia-button>
        <gaia-icon-button
          :dimension="28"
          quaternary
          title="Close"
          :style="{ flex: 'none' }"
          @click="emit('update:show', false)"
        >
          <n-icon :component="IconClose" :size="14" />
        </gaia-icon-button>
      </n-flex>
    </template>

    <!-- Edit: each version's state, set by hand. -->
    <n-flex v-if="editing" vertical :size="18">
      <n-flex
        v-for="revision in props.revisions"
        :key="revision.uid"
        align="center"
        justify="space-between"
        :size="8"
        :wrap="false"
      >
        <gaia-text size="row" weight="semibold" tone="ink">
          v{{ revision.version_number }}
        </gaia-text>
        <gaia-select
          size="small"
          :value="revision.document_state"
          :options="stateOptions"
          :disabled="isLocked(revision)"
          :title="isLocked(revision) ? 'Updating automatically' : undefined"
          :consistent-menu-width="false"
          :style="{ width: '200px' }"
          @update:value="(state: DocumentState) => emit('setState', revision.uid, state)"
        />
      </n-flex>
    </n-flex>

    <!-- History: the file's state, then its signing from creation on. -->
    <n-flex v-else vertical :size="10">
      <!-- The state under the name, so a long file name never squeezes it. -->
      <n-flex vertical :size="6">
        <gaia-text size="row" weight="semibold" tone="ink" truncate :title="props.fileName">
          {{ props.fileName }}
        </gaia-text>
        <gaia-contract-state-cell
          v-if="current"
          compact
          :state="current.effective_state"
          :reasons="current.needs_attention_reasons"
          :failure-reason="current.signature_failure_reason ?? null"
          :signature-infos="current.signature_infos ?? null"
        />
      </n-flex>
      <!-- Which request the steps below are of. -->
      <n-flex v-if="shown" vertical :size="2">
        <gaia-link
          :to="signingRequestRoute(shown.request.uid)"
          :underline="false"
          :title="shown.request.name ?? undefined"
        >
          {{ shown.request.name || 'Signing request' }}
        </gaia-link>
        <gaia-text v-if="requests.length > 1" size="micro" tone="faint">
          Signing request {{ requestIndex + 1 }} of {{ requests.length }}
        </gaia-text>
      </n-flex>
      <gaia-timeline :items="events" />
      <gaia-text v-if="props.signingHistory === null" size="meta" tone="faint">
        Loading signing requests…
      </gaia-text>
    </n-flex>
  </gaia-panel>
</template>
